import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import FieldTitle from '../FieldTitle';
import TiptapEditor from './TiptapEditor';

import Tooltip from '../../../misc/Tooltip';
import FieldModes from '../../../../constants/FieldModes';

const StyledWrapper = styled.div`
  width: 100%;
  ${(props) => (typeof props.margin === 'number' ? `padding: ${props.margin}px;` : '')}
  
  /*
   * Only use height when you don't need height 
   * measurements of the table field.
   */
  ${(props) => {
    return props.measure ? '' : 'height: 100%;';
  }}
`;

const StyledInternalWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StyledTooltipWrapper = styled.span`
  position: absolute;
  right:0;
  top:0;
`;

/**
 * @class RichTextField
 *
 * Rich Text Field 2.0 (Tiptap)
 *
 * Merge codes? If an object of { merge, content } is passed, we
 * populate the .content based on the merge codes. If a string is
 * passed, we assume it's the raw rte text value and use that only.
 *
 * Note we do preserve formatting with merge codes.
 *
 * {
 *  merge: { COMPANY_NAME: 'ACME Inc' },
 *  content: 'Hereby refered to as [COMPANY_NAME].'
 * } => 'Hereby refered to as ACME Inc.'
 *
 */
class RichTextField extends React.PureComponent {

  static propTypes = {

    fieldId: PropTypes.string,
    fieldIdentifier: PropTypes.string,
    fieldPositionId: PropTypes.string,

    title: PropTypes.string,
    tipTitle: PropTypes.string,
    tipDescription: PropTypes.string,
    tipVisible: PropTypes.bool,
    disabled: PropTypes.bool,

    wrapperStyles: PropTypes.object,

    //Title styles
    titleDisplay: PropTypes.string,
    titleFontSize: PropTypes.number,
    titleFontColor: PropTypes.string,
    titleTextAlign: PropTypes.string,
    titleFontStyle: PropTypes.string,
    titleFontWeight: PropTypes.string,
    titleTextTransform: PropTypes.string,
    titleTextDecoration: PropTypes.string,

    //Field style
    borderColor: PropTypes.string,
    borderRadius: PropTypes.number,
    borderWidth: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    margin: PropTypes.number,
    lineHeight: PropTypes.number,

    //Action handlers
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onMeasureCompleted: PropTypes.func,

    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),

    measure: PropTypes.bool

  }

  static defaultProps = {
    lineHeight: 10
  }

  constructor(props) {

    super(props);

    const { value } = this.props;

    this.state = {
      focused: false,
      changes: value,
      wrapperRef: null
    };
  }

  componentDidUpdate(prevProps) {

    const { changes, wrapperRef } = this.state;

    const { value, fieldId, fieldIdentifier, fieldPositionId, onMeasureCompleted, measure } = this.props;

    if (value !== prevProps.value && value !== changes) this.setState({ changes: value });
    if (measure && wrapperRef && onMeasureCompleted) {
      onMeasureCompleted(fieldId, fieldIdentifier, fieldPositionId, wrapperRef.getBoundingClientRect());
    }

  }

  componentWillUnmount() {

    const { value, disabled, onChange, fieldId, fieldIdentifier, fieldPositionId, onBlur } = this.props;
    const { changes, focused } = this.state;

    /**
     * This ensures that changes are saved when this component unmounts
     * without first losing focus. The return func in useEffect is the
     * same as firing cleanup stuff in comoponentWillUnmount.
     */
    if (focused && changes !== value && !disabled) {

      if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, changes);

      if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});

    }

  }

  render() {

    const {

      wrapperStyles,

      fieldId,
      fieldPositionId,

      title,
      tipTitle,
      tipDescription,
      tipVisible,
      disabled,
      mode,

      //Title styles
      titleDisplay,
      titleFontSize,
      titleFontColor,
      titleTextAlign,
      titleFontStyle,
      titleFontWeight,
      titleTextTransform,
      titleTextDecoration,

      //Field style
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      lineHeight,

      borderColor,
      borderRadius,
      borderWidth,
      backgroundColor,
      padding,
      margin,
      measure,

    } = this.props;

    const displayedValue = this.getDisplayValue();
    const isDisabled = mode === FieldModes.readonly || (mode !== FieldModes.edit && disabled);

    const getTooltip = () => {

      if (disabled) return null;

      return (
        <StyledTooltipWrapper>
          <Tooltip
            fieldId={fieldId}
            title={tipTitle}
            description={tipDescription}
            tipVisible={tipVisible}
          />
        </StyledTooltipWrapper>
      );

    };

    return (
      <StyledWrapper
        ref={(e) => {
          const { wrapperRef } = this.state;
          if (e && !wrapperRef) {
            this.setState({ wrapperRef: e });
          }
        }}
        margin={margin}
        measure={measure}
        style={wrapperStyles || {}}
      >
        <StyledInternalWrapper>
          <FieldTitle
            {...{
              title,
              titleDisplay,
              titleFontSize,
              titleFontColor,
              titleTextAlign,
              titleFontStyle,
              titleFontWeight,
              titleTextTransform,
              titleTextDecoration,
            }}
          />
          {getTooltip()}

          <TiptapEditor
            content={displayedValue}
            disabled={isDisabled}
            id={`${fieldPositionId}-richtext-tt-field`}
            onUpdate={this.handleEditorUpdate}
            onFocus={this.handleEditorFocus}
            onBlur={this.handleEditorBlur}
            {...{
              borderColor,
              borderRadius,
              borderWidth,
              backgroundColor,
              padding,
              fontSize,
              fontColor,
              fontStyle,
              fontWeight,
              textAlign,
              textTransform,
              textDecoration,
              textOverflow,
              lineHeight
            }}
          />
        </StyledInternalWrapper>
      </StyledWrapper>
    );
  }

  /**
   * Note: We orginally supported html with merge codes and now we support the below methods by doing
   * a quick 1 to 1 conversion of the html to json and then back again.
   *
   * 1. In edit mode, we just return the content for the editor to use
   * 2. In view/readonly mode, we process merge codes with our 1->1 conversion for parsing
   * 3. We still support plain string of html, html with merge codes, json, and json with merge codes
   */
  getDisplayValue = () => {
    const { changes } = this.state;
    const { mode } = this.props;

    return changes;
  }

  /**
   *
   * @param {Object} newValue { content: [{ type: 'paragraph', content: [{ type: 'text', text: 'test', marks:[{"type": "bold"}] }] }], type: 'doc' }
   */
  handleEditorUpdate = (newValue) => {
    const { onChange, fieldId, fieldIdentifier, fieldPositionId } = this.props;
    const { changes } = this.state;

    this.setState({ changes: newValue });

    if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, newValue);
  }

  handleEditorFocus = () => {
    const { focused } = this.state;
    const { disabled, onFocus, fieldId, fieldIdentifier, fieldPositionId } = this.props;
    if (!focused && !disabled) {
      this.setState({ focused: true });
      if (onFocus) onFocus(fieldId, fieldIdentifier, fieldPositionId);
    }
  }

  handleEditorBlur = () => {
    const { onBlur, fieldId, fieldIdentifier, fieldPositionId, disabled } = this.props;
    const { focused, changes } = this.state;
    if (focused && !disabled) {
      this.setState({ focused: false });
      if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, { value: changes });
    }
  }
}

export default RichTextField;
